import React from 'react';
import { mergeProps, useField } from 'react-aria';

import type { CheckboxInputProps } from '../../components/CheckboxInput/CheckboxInput';
import type { BaseFieldProps } from './common/types';
import { Alert } from '../../components/Alert/Alert';
import { Badge } from '../../components/Badge/Badge';
import { CheckboxInput } from '../../components/CheckboxInput/CheckboxInput';
import { styled } from '../../stitches.config';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import filterErrorMessage from '../../utils/filterErrorMessage';
import { FieldAnchorTarget, useFieldAnchor } from './common/FieldAnchor';
import { FieldDivider, fieldDividerDisplay } from './common/FieldDivider';
import { PrimaryFieldDescription, PrimaryFieldLabel } from './common/FieldLabel';
import { useCustomFieldProps } from './createCustomFieldPropsProvider';
import { MaybeFieldContainer } from './FieldContainer';
import { FieldErrorMessages } from './FieldError';

const PrimaryCheckboxFieldParent = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '$8',
  width: '100%',
});
PrimaryCheckboxFieldParent.displayName = 'PrimaryCheckboxFieldParent';

const PrimaryCheckboxFieldLabelling = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  gap: '$6',
});
PrimaryCheckboxFieldLabelling.displayName = 'PrimaryCheckboxFieldLabelling';

const PrimaryCheckboxFieldStart = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
});
PrimaryCheckboxFieldStart.displayName = 'PrimaryCheckboxFieldStart';

const PrimaryCheckboxFieldAlert = styled(Alert, {
  padding: '$8 $16',
});
PrimaryCheckboxFieldAlert.displayName = 'PrimaryCheckboxFieldAlert';

const PrimaryCheckboxFieldContainer = styled('div', FieldAnchorTarget, {
  zIndex: 1,
  display: 'flex',
  flexDirection: 'row',
  padding: '$12 $16',
  [fieldDividerDisplay]: 'none',

  '&:not(:last-child)': {
    paddingBottom: 0,
    [fieldDividerDisplay]: 'flex',
  },
});
PrimaryCheckboxFieldContainer.displayName = 'PrimaryCheckboxFieldContainer';

export interface PrimaryCheckboxFieldProps extends BaseFieldProps, CheckboxInputProps {
  internal?: boolean;
}

export function PrimaryCheckboxField(props: PrimaryCheckboxFieldProps) {
  const { fieldProps: customFieldProps, inputProps: customInputProps } = useCustomFieldProps();
  const anchor = useFieldAnchor(customFieldProps.name);

  const {
    description,
    errorMessage = customFieldProps.errorMessage,
    internal,
    label = customFieldProps.label,
    ...extraInputProps
  } = props;

  const {
    fieldProps: inputProps,
    descriptionProps,
    errorMessageProps,
    labelProps,
  } = useField({
    description,
    errorMessage,
    label,
  });

  return (
    <MaybeFieldContainer>
      <PrimaryCheckboxFieldContainer {...anchor.containerProps}>
        <PrimaryCheckboxFieldParent>
          <AlignStack
            direction="row"
            gap={8}
            preset="body"
            start={<CheckboxInput {...mergeProps(customInputProps, inputProps, extraInputProps)} />}
          >
            <PrimaryCheckboxFieldStart>
              <PrimaryCheckboxFieldLabelling>
                <PrimaryFieldLabel {...labelProps}>{label}</PrimaryFieldLabel>
                {internal && (
                  <Badge ends="card" size="small" internal>
                    Internal
                  </Badge>
                )}
                {anchor.link}
              </PrimaryCheckboxFieldLabelling>
              {description && (
                <PrimaryFieldDescription {...descriptionProps}>
                  {description}
                </PrimaryFieldDescription>
              )}
            </PrimaryCheckboxFieldStart>
          </AlignStack>
        </PrimaryCheckboxFieldParent>
        <FieldDivider context="primary" />
      </PrimaryCheckboxFieldContainer>
      {errorMessage && (
        <FieldErrorMessages>
          <PrimaryCheckboxFieldAlert
            {...errorMessageProps}
            type="inline"
            relation="stacked"
            variant="negative"
            heading={filterErrorMessage(errorMessage)}
          />
        </FieldErrorMessages>
      )}
    </MaybeFieldContainer>
  );
}
